import React from "react";
import { AbsoluteFill, Audio, interpolate, Sequence, staticFile } from "remotion";
import { INK, useAdFonts } from "./theme";
import { Scene1Hook } from "./Scene1Hook";
import { Scene2Solution } from "./Scene2Solution";
import { Scene3aNota } from "./Scene3aNota";
import { Scene3bKomisi } from "./Scene3bKomisi";
import { Scene4aLoyalty } from "./Scene4aLoyalty";
import { Scene4bAI } from "./Scene4bAI";
import { Scene5CTA } from "./Scene5CTA";

/* ════════════════════════════════════════════════════════════════════════
   IKLAN POS BARBERSHOP — 1080x1920 @30fps, 900 frame (30 detik), 5 scene.
   NEOBRUTALISM: border hitam tebal, drop shadow solid, tipografi ekstra tebal,
   warna latar kontras (kuning → cyan → hot pink → ungu → lime).

   Timeline:
     0-150   Scene 1 · Hook        (kuning)  notepad manual berantakan
     150-270 Scene 2 · Solution    (cyan)    dashboard tablet masuk dari bawah
     270-375 Scene 3a · Nota WA    (pink)    bubble WA + nota digital
     375-480 Scene 3b · Komisi     (pink)    koin terbelah + bar komisi
     480-585 Scene 4a · Loyalty    (ungu)    kartu stempel
     585-690 Scene 4b · AI WA      (ungu)    chat AI + grafik omset
     690-900 Scene 5 · CTA         (lime)    free trial + tombol + panah

   Audio:
     - Voice over Bahasa Indonesia (edge-tts) — satu file per scene di
       public/vo/<VOICE>/ supaya kalimatnya jatuh tepat di potongan scene.
     - SFX: "pop" di tiap potongan scene, "boom" di CTA (public/sfx/).
   ════════════════════════════════════════════════════════════════════════ */

/** Titik potong (frame global) — dipakai juga oleh Studio sebagai nama Sequence. */
export const T = {
  hook: 0,
  solution: 150,
  feature1: 270,
  feature2: 375,
  feature3: 480,
  feature4: 585,
  cta: 690,
  total: 900,
};

/** Folder suara voice over di public/vo/.
 *  "coral-full"  = openai/gpt-audio (model penuh) + coral, lewat OpenRouter — pilihan user.
 *  "coral"       = openai/gpt-audio-mini + coral (kurang nurut instruksi gaya).
 *  "id-ID-ArdiNeural" / "id-ID-GadisNeural" = edge-tts (fallback gratis). */
export const VOICE = "coral-full";

/** Voice over satu scene. */
const Vo: React.FC<{ file: string; volume?: number }> = ({ file, volume = 1 }) => (
  <Audio src={staticFile(`vo/${VOICE}/${file}.mp3`)} volume={volume} />
);

/** Efek suara potongan scene. */
const Sfx: React.FC<{ file: string; volume?: number }> = ({ file, volume = 0.5 }) => (
  <Audio src={staticFile(`sfx/${file}.mp3`)} volume={volume} />
);

/* ── Musik latar ──────────────────────────────────────────────────────────
   Bed musik 30 detik (bed.mp3, disintesis ffmpeg) + ducking manual: volume
   diturunkan otomatis saat voice over berbicara supaya narasi tetap jelas.
   [mulai, selesai] dalam frame global — panjang tiap klip VO × 30fps. */
const VO_WINDOWS: Array<[number, number]> = [
  [0, 112], // s1_hook     3.73s
  [150, 246], // s2_solution 3.21s
  [270, 357], // s3a_nota    2.90s
  [375, 452], // s3b_komisi  2.55s
  [480, 554], // s4a_loyalty 2.45s
  [585, 671], // s4b_ai      2.85s
  [690, 830], // s5_cta      4.66s
];
const MUSIC_LOUD = 0.3; // saat jeda bicara
const MUSIC_DUCK = 0.14; // saat voice over bicara

const musicVolume = (frame: number): number => {
  let v = MUSIC_LOUD;
  for (const [start, end] of VO_WINDOWS) {
    const inside = interpolate(frame, [start - 8, start, end, end + 8], [0, 1, 1, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
    v = Math.min(v, MUSIC_LOUD + (MUSIC_DUCK - MUSIC_LOUD) * inside);
  }
  return v;
};

export const BarbershopAd: React.FC = () => {
  useAdFonts();

  return (
    <AbsoluteFill style={{ backgroundColor: INK }}>
      {/* ── Musik latar sepanjang 30 detik (volume turun saat voice over) ── */}
      <Audio src={staticFile("music/bed.mp3")} volume={musicVolume} />

      {/* ── 1. Hook ── */}
      <Sequence from={T.hook} durationInFrames={150} name="1 · Hook (nyatet manual)">
        <Sfx file="pop" />
        <Vo file="s1_hook" />
        <Scene1Hook />
      </Sequence>

      {/* ── 2. Solusi ── */}
      <Sequence from={T.solution} durationInFrames={120} name="2 · Solusi (dashboard)">
        <Sfx file="pop" />
        <Vo file="s2_solution" />
        <Scene2Solution />
      </Sequence>

      {/* ── 3a. Nota digital WA & print ── */}
      <Sequence from={T.feature1} durationInFrames={105} name="3a · Nota WA & Print">
        <Sfx file="pop" />
        <Vo file="s3a_nota" />
        <Scene3aNota />
      </Sequence>

      {/* ── 3b. Komisi kapster otomatis ── */}
      <Sequence from={T.feature2} durationInFrames={105} name="3b · Komisi kapster">
        <Sfx file="pop" />
        <Vo file="s3b_komisi" />
        <Scene3bKomisi />
      </Sequence>

      {/* ── 4a. Loyalty ── */}
      <Sequence from={T.feature3} durationInFrames={105} name="4a · Loyalty 5x gratis 1x">
        <Sfx file="pop" />
        <Vo file="s4a_loyalty" />
        <Scene4aLoyalty />
      </Sequence>

      {/* ── 4b. AI assistant owner ── */}
      <Sequence from={T.feature4} durationInFrames={105} name="4b · AI assistant via WA">
        <Sfx file="pop" />
        <Vo file="s4b_ai" />
        <Scene4bAI />
      </Sequence>

      {/* ── 5. CTA ── */}
      <Sequence from={T.cta} durationInFrames={210} name="5 · CTA free trial">
        <Sfx file="boom" volume={0.6} />
        <Vo file="s5_cta" />
        {/* SFX kecil saat panah + link muncul (frame 690+96 = 26,2s) */}
        <Sequence from={96} durationInFrames={20} name="sfx · panah">
          <Sfx file="pop" volume={0.45} />
        </Sequence>
        <Scene5CTA />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
